import { Component, inject, input, OnInit, output, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconComponentV2 } from '../../../../../../shared/components/icon-component/icon.component';
import { MicroServiceHttpService } from '../../../../../../core/services/microServiceHttp.service';
import { finalize } from 'rxjs/operators';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmationModalComponent, ConfirmationModalData } from '../../../../../../shared/components/confirmation-modal/confirmation-modal.component';

export interface ChatbotTool {
  pid: number;
  chatbotId: number;
  toolType: 'DATABASE' | 'API';
  name: string;
  description: string;
  guardrails: string;
  mysqlQuery: string | null;
  apiUrl: string | null;
  apiMethod: string | null;
  apiHeaders: Record<string, string> | null;
  apiBody: Record<string, unknown> | null;
  apiParams: Record<string, unknown> | null;
  apiQuery: Record<string, unknown> | null;
  apiAuthToken: string | null;
  apiAuthType: string | null;
  apiAuthKeyname: string;
  isGlobal: boolean;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-tools',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponentV2, MatDialogModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './tools.component.html'
})
export class ToolsComponent implements OnInit {
  private readonly http = inject(MicroServiceHttpService);
  private readonly dialog = inject(MatDialog);

  readonly botId = input<number | null>(null);

  /** Fires when the user clicks "Create Tool" so the parent can render the drawer outside the stacking context. */
  readonly createToolClicked = output<void>();
  readonly editToolClicked = output<ChatbotTool>();

  // ── List state ──────────────────────────────────────────────
  tools = signal<ChatbotTool[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);
  searchQuery = signal('');
  filterType = signal<'ALL' | 'DATABASE' | 'API'>('ALL');
  expandedPid = signal<number | null>(null);

  readonly filteredTools = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const type = this.filterType();
    return this.tools().filter(t => {
      const matchesType = type === 'ALL' || t.toolType === type;
      const matchesSearch = !q || t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
      return matchesType && matchesSearch;
    });
  });

  readonly dbCount = computed(() => this.tools().filter(t => t.toolType === 'DATABASE').length);
  readonly apiCount = computed(() => this.tools().filter(t => t.toolType === 'API').length);

  // ── Lifecycle ─────────────────────────────────────────────────
  ngOnInit(): void {
    const id = this.botId();
    if (id == null) return;
    this.loadTools(id);
  }

  // ── List methods ──────────────────────────────────────────────
  loadTools(chatbotId: number): void {
    this.loading.set(true);
    this.error.set(null);
    this.http.get<unknown>('whitelabel', `tools/chatbot/${chatbotId}?includeNodeCount=true&includeNodes=true`, { showToast: false, showLoader: false })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res: any) => {
          const data = res?.data ?? res ?? [];
          this.tools.set(Array.isArray(data) ? data : []);
        },
        error: () => this.error.set('Failed to load tools. Please try again.')
      });
  }

  toggleExpand(pid: number): void {
    this.expandedPid.update(current => current === pid ? null : pid);
  }

  setFilter(type: 'ALL' | 'DATABASE' | 'API'): void {
    this.filterType.set(type);
  }

  // ── Drawer triggers ──────────────────────────────────────────────
  onCreateTool(): void {
    this.createToolClicked.emit();
  }

  onEditTool(tool: ChatbotTool, event: Event): void {
    event.stopPropagation();
    this.editToolClicked.emit(tool);
  }

  deleteTool(tool: ChatbotTool, event: Event): void {
    event.stopPropagation();
    
    this.dialog.open(ConfirmationModalComponent, {
      width: '400px',
      data: {
        title: 'Delete Tool',
        description: `Are you sure you want to delete tool "${tool.name}"?`,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        variant: 'danger'
      } as ConfirmationModalData
    }).afterClosed().subscribe((confirmed: boolean | { confirmed: boolean }) => {
      const isConfirmed = typeof confirmed === 'object' ? confirmed?.confirmed : confirmed;
      if (!isConfirmed) return;

      this.http.delete<unknown>('whitelabel', `tools/${tool.pid}`, { showToast: true, successMessage: 'Tool deleted successfully', showLoader: true })
        .subscribe({
          next: () => {
            const id = this.botId();
            if (id != null) {
              this.loadTools(id);
            }
          }
        });
    });
  }
}

